import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Router, RouterLink } from "@angular/router";
import { FUNCTION_RESULT_STATUS, ICustomer } from "@nova/common-interfaces";
import { IInvoice } from "@rfm/shared-interfaces";
import { BlockUIModule } from "ng-block-ui";
import { MenuItem, MessageService } from "primeng/api";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { MenuModule } from "primeng/menu";
import { SelectModule } from "primeng/select";
import { SelectButtonModule } from "primeng/selectbutton";
import {
  Table,
  TableModule,
  TableRowCollapseEvent,
  TableRowExpandEvent,
} from "primeng/table";
import { TabMenuModule } from "primeng/tabmenu";
import { CommonComponent } from "../../../../../../lib/components/index.mjs";
import { InvoiceService } from "../../../../../shared/services/financial/invoice.service.mjs";

@Component({
  selector: "app-invoice-list",
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    TableModule,
    BlockUIModule,
    MenuModule,
    TabMenuModule,
    InputIconModule,
    ButtonModule,
    IconFieldModule,
    BreadcrumbModule,
    FormsModule,
    ReactiveFormsModule,
    SelectButtonModule,
    SelectModule,
  ],
  providers: [MessageService],
  templateUrl: "./invoice-list.component.html",
  styleUrls: ["./invoice-list.component.scss"],
})
export class InvoiceListComponent extends CommonComponent implements OnInit {
  invoices: IInvoice[] = [];
  filtered_invoices: IInvoice[] = [];
  activeItem: IInvoice | undefined;
  activeTab: any;
  searchValue: string | undefined;

  dateOptions: string[] = [
    "Last day",
    "Last 3 days",
    "Last 7 days",
    "Last 14 days",
    "This month",
    "Last Month",
  ];
  selectedDate: string = "Last 7 days";

  tabs: MenuItem[] = [
    { label: "All", command: () => this.setActiveTab("All") },
    { label: "Paid", command: () => this.setActiveTab("Paid") },
    { label: "Pending", command: () => this.setActiveTab("Pending") },
    { label: "Outstanding", command: () => this.setActiveTab("Outstanding") },
    { label: "Cancelled", command: () => this.setActiveTab("Cancelled") },
  ];

  breadCrumbItems: MenuItem[] | undefined;

  home: MenuItem | undefined;

  addInvoice() {
    this.router.navigateByUrl("/admin/invoices/edit/" + 0);
  }

  expanded: boolean = false;

  expandedRows = {};

  expandAll() {
    this.expandedRows = this.invoices.reduce(
      (acc: any, p) => (acc[p.id] = true) && acc,
      {}
    );
  }

  collapseAll() {
    this.expandedRows = {};
  }

  onRowExpand(event: TableRowExpandEvent) {
    this.messageService.add({
      severity: "info",
      summary: "Product Expanded",
      detail: event.data.name,
      life: 3000,
    });
  }

  onRowCollapse(event: TableRowCollapseEvent) {
    this.messageService.add({
      severity: "success",
      summary: "Product Collapsed",
      detail: event.data.name,
      life: 3000,
    });
  }

  clear(table: Table) {
    table.clear();
    this.searchValue = "";
  }

  filter(event: any, table: Table) {
    table.filterGlobal(event.target.value, "contains");
  }

  setActiveTab(label: string) {
    this.activeTab = label;

    switch (label.toLowerCase()) {
      case "all":
        this.filtered_invoices = this.invoices.filter(
          (x) => x.is_deleted !== true
        );
        break;
      case "paid":
        this.filtered_invoices = this.invoices.filter(
          (x) => x.status === "PAID" && x.is_deleted !== true
        );
        break;
      case "pending":
        this.filtered_invoices = this.invoices.filter(
          (x) => x.status === "Pending" && x.is_deleted !== true
        );
        break;
      case "cancelled":
        this.filtered_invoices = this.invoices.filter(
          (x) => x.is_deleted === true
        );
        break;
      case "outstanding":
        this.filtered_invoices = this.invoices.filter(
          (x) => x.status !== "PAID" && x.is_deleted !== true
        );
        break;
    }
  }

  items: MenuItem[] = [
    {
      label: "View",
      icon: "pi pi-file",
      command: () => this.view(this.activeItem!.id),
    },
    {
      label: "Edit",
      icon: "pi pi-file-edit",
      command: () => this.edit(this.activeItem!.id),
    },
    {
      label: "View Customer",
      icon: "pi pi-share-alt",
      command: () => this.showAll(this.activeItem!.customer!.id),
    },
  ];
  view(invoice_id: string) {
    this.router.navigateByUrl("/admin/invoices/view/" + invoice_id);
  }

  edit(invoice_id: string) {
    this.router.navigateByUrl("/admin/invoices/edit/" + invoice_id);
  }

  showAll(customer_id: string) {
    this.router.navigateByUrl("/admin/customers/view/" + customer_id);
  }

  loading: boolean = true;

  constructor(
    protected router: Router,
    protected invoiceService: InvoiceService,
    private messageService: MessageService
  ) {
    super();
    this.isLoading$.subscribe((state) => {
      this.loading = state;
    });
    this.isLoading(true);
  }

  selectedInvoice: IInvoice | undefined;

  rowSelected() {
    console.log("Invoice", this.selectedInvoice);
    this.router.navigate(["/admin/invoices/view/", this.selectedInvoice?.id]);
  }

  getGaiiaData(customer: ICustomer) {
    let result = "";

    if (customer.reference_keys && customer.reference_keys.length > 0) {
      result = customer.reference_keys.data[0].referenceId;
    }

    return result;
  }

  error = false;

  ngOnInit() {
    this.breadCrumbItems = [{ label: "Invoices" }];

    this.home = { icon: "pi pi-home", routerLink: "/" };

    this.loaddata();
  }

  loaddata() {
    this.error = false;
    this.isLoading(true);

    this.invoices = [];
    this.filtered_invoices = { ... this.invoices};

    let start = 0;
    let end = 0;

    const today = new Date();
    today.setHours(23, 59, 59, 999); //Set milliseconds to 999
    end = today.getTime();

    switch (this.selectedDate.toLowerCase()) {
      case "last day":
        // Calculate the date 7 days prior.
        const oneDayBefore = new Date(today.setDate(today.getDate() - 1));

        // Set the time to midnight (00:00:00).
        oneDayBefore.setHours(0, 0, 0, 0);

        // Get the Unix timestamp in milliseconds.
        start = oneDayBefore.getTime();
        break;
      case "last 3 days":
        start = this.setStartDate(3);
        break;
      case "last 7 days":
        start = this.setStartDate(7);
        break;
      case "last 14 days":
        start = this.setStartDate(14);
        break;
      case "this month":
        // Calculate the date 7 days prior.
        const firstOfMonth = new Date(today.setDate(today.getDate()));

        // Set the time to midnight (00:00:00).
        firstOfMonth.setHours(0, 0, 0, 0);
        firstOfMonth.setDate(1);

        // Get the Unix timestamp in milliseconds.
        start = firstOfMonth.getTime();
        break;
      case "last month":
        // Calculate the date 7 days prior.
        const firstOfPriorMonth = new Date(today.setDate(today.getDate()));

        // Set the time to midnight (00:00:00).
        firstOfPriorMonth.setHours(0, 0, 0, 0);
        firstOfPriorMonth.setDate(1);
        firstOfPriorMonth.setMonth(firstOfPriorMonth.getMonth() - 1);

        start = firstOfPriorMonth.getTime();

        // Subtract one day to get the last day of the previous month.
        let lastDayOfPreviousMonth = new Date(
          firstOfPriorMonth.setMonth(firstOfPriorMonth.getMonth() + 1)
        ).getTime();

        const zero = new Date(lastDayOfPreviousMonth);

        zero.setDate(zero.getDate() - 1);
        zero.setHours(23, 59, 59, 999);

        end = zero.getTime();

        console.log("Starting date", new Date(start));
        console.log("Ending date", new Date(end));

        break;
    }

    this.invoiceService
      .getByDateRange(start, end)
      .then((result) => {
        if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
          this.invoices = result.data!;
          this.setActiveTab("all");
        }
      })
      .catch((err) => {
        this.error  =true;
        console.error("Error getting customer data", err);
      })
      .finally(() => {
        this.isLoading(false);
      });
  }

  setStartDate(days: number): number {
    const today = new Date();
    today.setHours(23, 59, 59, 999); //Set milliseconds to 999

    // Calculate the date 7 days prior.
    const sevenDaysBefore = new Date(today.setDate(today.getDate() - days));

    // Set the time to midnight (00:00:00).
    sevenDaysBefore.setHours(0, 0, 0, 0);

    // Get the Unix timestamp in milliseconds.
    return sevenDaysBefore.getTime();
  }

  valueChanged() {
    this.loaddata();
  }
}
